import { Col, Modal, Row } from 'antd';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import identifikaceEmitenta, { IIdentifikaceEmitenta } from 'services/akcionar/emise/identifikaceEmitenta';
import { IEmisniUdaje } from 'services/akcionar/emise/novyIsinSend';
import { IRozcestnikData } from 'services/akcionar/emise/rozcestnikISIN';
import { IisinZmena } from 'services/akcionar/emise/zmenaISIN/zadostZmena';
import { vyjadreniCdcpIsinApi } from 'services/spravaEmitenta/vyjadreniCdcp';

import { ERozcestnikOptions } from 'enums/isin/rozcestnikOptions';
import { EDruhCp } from 'enums/isin/udaje/druhCp';
import routePathEnum from 'enums/routePathEnum';

import { IRozcestnikInput } from '../novyISIN/IdentifikaceEmitentaNovy';
import UdajeEmise from '../novyISIN/UdajeEmise';
import ZadostPosila from '../novyISIN/ZadostPosila';
import DuvodZmeny from './DuvodZruseni';
import IdentifikaceEmitentaZmena from './IdentifikaceEmitentaZmena';
import RozcestnikZmena from './RozcestnikZmena';

interface IZmenaISIN {}

const ZruseniISIN: React.FC<IZmenaISIN> = () => {
    const { isin, cisStaty, cisMeny, aktivniFirma } = useContext(Context);
    const [errStatus, setErrStatus] = useState<number>();
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [identifikacePutData, setIdentifikacePutData] = useState<IIdentifikaceEmitenta>();
    const [zadostID, setZadostID] = useState<number | undefined>(isin ? isin.idZadosti : 0);
    const [rozcestnikData, setRozcestnikData] = useState<IRozcestnikData['spolecnost']>();
    const [rozcestnikInput, setRozcestnikInput] = useState<IRozcestnikInput | undefined>();
    const [typZruseni, setTypZruseni] = useState<boolean>(window.location.href.includes('emise-zruseni-isin'));
    const [loading, setLoading] = useState<boolean>(false);
    //  const [finish, setFinish] = useState<boolean>(false);
    const [druhCP, setDruhCP] = useState<EDruhCp | undefined>();
    const [udajeEmise, setUdajeEmise] = useState<IEmisniUdaje | undefined>();
    const prevStepRef = useRef(0);
    const [vyjadreni, setVyjadreni] = useState<string>('');
    const [modalMessage, setModalMessage] = useState<string>('');
    const [firstRender, setFirstRender] = useState<boolean>(true);
    const [zruseniData, setZruseniData] = useState<IisinZmena>();

    const next = useCallback(() => {
        setCurrentStep(currentStep + 1);
    }, [currentStep]);

    const prev = () => {
        setCurrentStep(currentStep - 1);
    };

    useEffect(() => {
        setTypZruseni(window.location.href.includes('emise-zruseni-isin'));
        if (firstRender) {
            setFirstRender(false);
        } else {
            setCurrentStep(0);
            setIdentifikacePutData(undefined);
            setZadostID(undefined);
            setRozcestnikData(undefined);
            setUdajeEmise(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [window.location.href]);

    useEffect(() => {
        if (isin?.stav === 'kdoplneni') {
            // zobrazi vyjadrenie cdcp v modale
            setLoading(true);
            vyjadreniCdcpIsinApi(isin.idZadosti ? isin.idZadosti : 0)
                .then(data => setVyjadreni(data.vyjadreni))
                .finally(() => {
                    setLoading(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        zadostID && setCurrentStep(1); // ak je zadost id = rozpracovana ziadost => prejde na dalsiu stranu
    }, [zadostID]);

    useEffect(() => {
        identifikacePutData &&
            zadostID &&
            identifikaceEmitenta('POST', zadostID, identifikacePutData, typZruseni ? 'Zruseni' : 'Zmena')
                .then(() => {
                    setLoading(true);
                    currentStep === 1 && next();
                })
                .catch((err: any) => {
                    setErrStatus(err.response?.status);
                })
                .finally(() => {
                    setLoading(false);
                });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identifikacePutData, typZruseni, zadostID]);

    /*  useEffect(() => {
        zmenaDuvod?.jmeno &&
            zadostID &&
            zadostZmenaPut(zmenaDuvod, zadostID, typZruseni ? 'Zruseni' : 'Zmena')
                .then(() => {
                    setLoading(false);
                })
                .catch((err: any) => {
                    setErrStatus(err.response?.status);
                })
                .finally(() => {
                    setLoading(false);
                    setFinish(true);
                });
    }, [typZruseni, zadostID, zmenaDuvod]); */

    const steps = [
        {
            title: '',
            content: (
                <RozcestnikZmena
                    cisStaty={cisStaty}
                    rozcestnikData={rozcestnikData}
                    setRozcestnikData={setRozcestnikData}
                    setRozcestnikInput={setRozcestnikInput}
                    nextStep={next}
                    setZadostID={setZadostID}
                    identifikacePutData={identifikacePutData}
                    typZruseni={typZruseni}
                />
            )
        },
        ...(typZruseni
            ? [
                  {
                      title: '',
                      content: (
                          <IdentifikaceEmitentaZmena
                              cisStaty={cisStaty}
                              cisMeny={cisMeny}
                              rozpracovanaZadost={!!isin}
                              rozcestnikData={rozcestnikData}
                              rozcestnikInput={rozcestnikInput}
                              prevStep={prev}
                              identifikacePutData={identifikacePutData}
                              setIdentifikacePutData={setIdentifikacePutData}
                          />
                      )
                  },
                  {
                      title: '',
                      content: (
                          <DuvodZmeny
                              prevStep={prev}
                              nextStep={next}
                              zadostID={zadostID!}
                              zruseniData={zruseniData}
                              setZruseniData={setZruseniData}
                          />
                      )
                  }
              ]
            : [
                  {
                      title: '',
                      content: (
                          <IdentifikaceEmitentaZmena
                              cisStaty={cisStaty}
                              cisMeny={cisMeny}
                              rozpracovanaZadost={!!isin}
                              rozcestnikData={rozcestnikData}
                              rozcestnikInput={rozcestnikInput}
                              prevStep={prev}
                              identifikacePutData={identifikacePutData}
                              setIdentifikacePutData={setIdentifikacePutData}
                          />
                      )
                  },
                  {
                      title: 'ISIN_EMISE_UDAJ_NADPIS',
                      content: (
                          <UdajeEmise
                              zmenaIsin={true}
                              cisMeny={cisMeny}
                              setIdentifikacePutData={setIdentifikacePutData}
                              identifikacePutData={identifikacePutData}
                              rozcestnikInput={rozcestnikInput}
                              nextStep={next}
                              prevStep={prev}
                              zadostID={zadostID}
                              druhCP={druhCP}
                              setDruhCP={setDruhCP}
                              setUdajeEmise={setUdajeEmise}
                              udajeEmise={udajeEmise}
                              prevStepRef={prevStepRef.current}
                              setRozcestnikInput={setRozcestnikInput}
                              setModalMessage={setModalMessage}
                          />
                      )
                  }
              ]),
        {
            title: '',
            content: (
                <ZadostPosila
                    zruseniData={zruseniData}
                    prevStep={prev}
                    zadostID={zadostID}
                    druhCP={druhCP}
                    udajeEmise={udajeEmise}
                    mode={typZruseni ? ERozcestnikOptions.ZRUSIT : ERozcestnikOptions.ZMENA}
                />
            )
        }
    ];

    return (
        <>
            {loading || errStatus ? (
                <Row
                    justify="center"
                    align="middle"
                    style={{
                        height: '100%'
                    }}
                >
                    <Col span={16} flex={'0 0 auto'}>
                        <LoadingWrapper loading={loading}>
                            {errStatus === 404 ? (
                                <Title text="RESPONSE_ERROR_404" level={3} />
                            ) : errStatus ? (
                                <Title text="SLUZBA_NEDOSTUPNA" level={3} />
                            ) : null}
                        </LoadingWrapper>
                    </Col>
                </Row>
            ) : (
                <LoadingWrapper loading={loading}>
                    <Title level={3} text={typZruseni ? 'FORMULARE_ZADOST_ZRUS' : 'FORMULARE_ZADOST_ZMEN'} />
                    <Title level={4} text={steps[currentStep].title} />
                    <Row gutter={8}>
                        <Col span={24}>{steps[currentStep].content}</Col>
                    </Row>
                </LoadingWrapper>
            )}
            <Modal
                title={<TranslateText>{'VYJADRENI_CDCP'}</TranslateText>}
                open={!!vyjadreni}
                onCancel={() => setVyjadreni('')}
                afterClose={() => {
                    setVyjadreni('');
                }}
                footer={
                    <Row className="button-row-modal">
                        <ButtonComponent
                            style={{
                                marginTop: 20
                            }}
                            id="isinZavritBtn"
                            buttontext="ZAVRIT"
                            onClick={() => {
                                setVyjadreni('');
                            }}
                        />
                    </Row>
                }
            >
                <Row>
                    <Paragraph text={vyjadreni || ''} />
                </Row>
            </Modal>
            <Modal
                open={!!modalMessage}
                onCancel={() => setModalMessage('')}
                afterClose={() => {
                    setModalMessage('');
                }}
                footer={
                    <Row className="button-row-modal">
                        <ButtonComponent
                            id="isinZavritBtn"
                            buttontext="ZAVRIT"
                            onClick={() => {
                                setModalMessage('');
                            }}
                            type="default"
                        />
                        <AnchorComponent
                            link={true}
                            to={`/firma/${aktivniFirma?.iducet}${routePathEnum.AMLZ_FORMULAR}`}
                        >
                            <ButtonComponent
                                id="isinModalAmlzRedirectBtn"
                                buttontext="PREJIT_NA_AMLZ"
                                onClick={() => {
                                    setModalMessage('');
                                }}
                            />
                        </AnchorComponent>
                    </Row>
                }
            >
                <Row>
                    <Paragraph text={modalMessage || ''} />
                </Row>
            </Modal>
        </>
    );
};

export default ZruseniISIN;
